/**
 * Types for the get_admin_students RPC
 * Used by StudentManagementAdmin to fetch data with server-side filtering, sorting, and pagination
 */

import { ApplicationStatus } from './application';

/**
 * Search type options for the admin students search
 */
export type AdminSearchType = 'name' | 'mars_code' | 'email' | 'phone' | 'grade' | 'status' | 'all';

/**
 * Parameters for the get_admin_students RPC
 */
export interface GetAdminStudentsParams {
  // School targeting - use one or the other, or neither for all schools
  p_school_id?: string | null;
  p_school_ids?: string[] | null;

  // Core filters
  p_academic_year?: string | null;
  p_statuses?: string[] | null;

  // Search
  p_search_query?: string | null;
  p_search_type?: AdminSearchType;

  // Filters
  p_grades?: string[] | null;
  p_cities?: string[] | null;
  p_date_start?: string | null;
  p_date_end?: string | null;
  p_school_choices?: string[] | null;
  p_has_sibling?: boolean | null;
  p_is_staff?: boolean | null;
  p_needs_transportation?: boolean | null;

  // Sorting
  p_sort_field?: string;
  p_sort_direction?: 'asc' | 'desc';

  // Pagination
  p_limit?: number;
  p_offset?: number;
}

/**
 * Raw row returned from the get_admin_students RPC
 */
export interface AdminStudentRow {
  // Core fields
  id: string;
  student_first_name: string;
  student_middle_name: string | null;
  student_last_name: string;
  mars_code: string | null;
  date_of_birth: string | null;
  current_grade: string | null;
  applying_grade: string;
  gender: string | null;
  ethnic_background: string | null;
  status: string;
  confirmation_code: string;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  academic_year: string;

  // Parent info
  parent_first_name: string;
  parent_last_name: string;
  parent_email: string;
  parent_phone: string;
  parent_relationship: string | null;

  // Address
  address: string;
  apartment_number: string | null;
  city: string;
  state: string;
  zip_code: string;
  home_district: string | null;
  boundary_school: string | null;

  // School references (joined)
  first_choice_id: string | null;
  first_choice_name: string | null;
  first_choice_session: string | null;
  second_choice_id: string | null;
  second_choice_name: string | null;
  second_choice_session: string | null;
  placed_school_id: string | null;
  placed_school_name: string | null;
  placed_session: string | null;
  current_school: string | null;
  school_type: string | null;
  previous_school_name: string | null;
  came_from: string | null;
  boundary_grade: string | null;
  next_boundary_school: string | null;
  first_enrolled_at: string | null;

  // Flags
  has_sibling: boolean;
  is_staff: boolean | null;
  parent_is_staff: boolean | null;
  is_out_of_district: boolean | null;

  // Academic info
  has_iep: boolean | null;
  english_language_learner: boolean | null;

  // Transportation
  needs_transportation: boolean;
  transportation_type: string | null;
  transportation_status: string | null;
  special_transportation_needs: string | null;
  alternate_pickup_address: string | null;
  alternate_dropoff_address: string | null;
  transportation_metadata: Record<string, any> | null;

  // Dates
  application_date: string | null;

  // Other
  notes: string | null;
  application_source: string | null;

  // Withdrawal & lifecycle
  withdrawal_date: string | null;
  withdrawal_reason: string | null;
  program_end_date: string | null;
  exclude_from_rollover: boolean | null;
  withdrawal_academic_year: string | null;

  // Pagination info
  total_count: number;
}

/**
 * Transformed student matching the Student interface used in StudentManagementAdmin
 * This is what the UI components consume
 */
export interface TransformedAdminStudent {
  [key: string]: unknown;
  id: string;
  student_first_name: string;
  student_middle_name?: string;
  student_last_name: string;
  mars_code?: string;
  date_of_birth: string;
  current_grade: number | string;
  applying_grade: number | string;
  parent_email: string;
  parent_phone: string;
  status: string;
  placed_school?: string;
  schools?: {
    name: string;
  };
  academic_year: string;
  history?: TransformedAdminStudent[];
  hasHistory?: boolean;

  // Application-specific fields
  created_at: string;
  submitted_at?: string;
  confirmation_code: string;
  first_choice: string;
  second_choice?: string;
  has_sibling?: boolean;
  is_staff?: boolean;
  parent_is_staff?: boolean;
  needs_transportation?: boolean;
  is_out_of_district?: boolean;
  application_date?: string;

  // Parent info
  parent_first_name: string;
  parent_last_name: string;
  parent_relationship?: string;

  // Address info
  address: string;
  city: string;
  state: string;
  zip_code: string;
  apartment_number?: string;
  home_district?: string;

  // Personal info
  gender?: string;
  ethnic_background?: string;

  // School info
  current_school?: string;
  boundary_school?: string;
  school_type?: string;
  first_choice_session?: string;
  second_choice_session?: string;
  placed_session?: string;

  // Academic info
  has_iep?: boolean;
  english_language_learner?: boolean;

  // Transportation
  transportation_status?: string;
  transportation_type?: string;
  alternate_pickup_address?: string;
  alternate_dropoff_address?: string;
  special_transportation_needs?: string;
  transportation_metadata?: Record<string, any>;

  // Notes
  notes?: string;
  application_source?: string;

  // Withdrawal & lifecycle
  withdrawal_date?: string;
  withdrawal_reason?: string;
  program_end_date?: string;
  exclude_from_rollover?: boolean;
  withdrawal_academic_year?: string;
}

/**
 * Transform raw RPC row to match existing Student interface in StudentManagementAdmin
 */
export function transformAdminStudentRow(row: AdminStudentRow): TransformedAdminStudent {
  return {
    id: row.id,
    student_first_name: row.student_first_name,
    student_middle_name: row.student_middle_name || undefined,
    student_last_name: row.student_last_name,
    mars_code: row.mars_code || undefined,
    date_of_birth: row.date_of_birth || '',
    current_grade: row.current_grade || '',
    applying_grade: row.applying_grade,
    parent_email: row.parent_email,
    parent_phone: row.parent_phone,
    status: row.status,
    placed_school: row.placed_school_id || undefined,
    schools: row.placed_school_name ? { name: row.placed_school_name } : undefined,
    academic_year: row.academic_year,
    hasHistory: false,

    created_at: row.created_at,
    submitted_at: row.submitted_at || undefined,
    confirmation_code: row.confirmation_code,
    first_choice: row.first_choice_id || '',
    second_choice: row.second_choice_id || undefined,
    has_sibling: row.has_sibling,
    is_staff: row.is_staff || undefined,
    parent_is_staff: row.parent_is_staff || undefined,
    needs_transportation: row.needs_transportation,
    is_out_of_district: row.is_out_of_district || undefined,
    application_date: row.application_date || undefined,

    parent_first_name: row.parent_first_name,
    parent_last_name: row.parent_last_name,
    parent_relationship: row.parent_relationship || undefined,

    address: row.address,
    city: row.city,
    state: row.state,
    zip_code: row.zip_code,
    apartment_number: row.apartment_number || undefined,
    home_district: row.home_district || undefined,

    gender: row.gender || undefined,
    ethnic_background: row.ethnic_background || undefined,

    current_school: row.current_school || undefined,
    boundary_school: row.boundary_school || undefined,
    school_type: row.school_type || undefined,
    previous_school_name: row.previous_school_name || undefined,
    came_from: row.came_from || undefined,
    boundary_grade: row.boundary_grade || undefined,
    next_boundary_school: row.next_boundary_school || undefined,
    first_enrolled_at: row.first_enrolled_at || undefined,
    first_choice_session: row.first_choice_session || undefined,
    second_choice_session: row.second_choice_session || undefined,
    placed_session: row.placed_session || undefined,

    has_iep: row.has_iep || undefined,
    english_language_learner: row.english_language_learner || undefined,

    transportation_status: row.transportation_status || undefined,
    transportation_type: row.transportation_type || undefined,
    alternate_pickup_address: row.alternate_pickup_address || undefined,
    alternate_dropoff_address: row.alternate_dropoff_address || undefined,
    special_transportation_needs: row.special_transportation_needs || undefined,
    transportation_metadata: row.transportation_metadata || undefined,

    notes: row.notes || undefined,
    application_source: row.application_source || undefined,

    withdrawal_date: row.withdrawal_date || undefined,
    withdrawal_reason: row.withdrawal_reason || undefined,
    program_end_date: row.program_end_date || undefined,
    exclude_from_rollover: row.exclude_from_rollover ?? undefined,
    withdrawal_academic_year: row.withdrawal_academic_year || undefined,
  };
}

/**
 * Default statuses for the "All Students" (enrolled) tab
 */
export const ENROLLED_STATUSES: ApplicationStatus[] = ['enrolled', 'placed', 'accepted', 'confirmed', 'pending_withdrawal'];

/**
 * Default statuses for the "New Applications" tab: applications a family has submitted and
 * that are still being decided. Drafts are not submitted yet; the waiting pool is still open.
 */
export const APPLICATION_STATUSES: ApplicationStatus[] = [
  'submitted',
  'processing',
  'under_review',
  'pending_acceptance',
  'waiting_pool',
];

/**
 * All possible statuses grouped for the filter UI
 */
export const STATUS_GROUPS = {
  Active: ['enrolled', 'placed', 'accepted', 'confirmed', 'scheduled', 'pending_withdrawal'] as ApplicationStatus[],
  Applications: ['draft', 'submitted', 'processing', 'under_review', 'pending_acceptance'] as ApplicationStatus[],
  Waiting: ['waitlisted', 'waiting_pool', 'declined', 'rejected'] as ApplicationStatus[],
  Inactive: ['withdrawn', 'program_ended', 'archived'] as ApplicationStatus[],
};

/**
 * Default pagination values
 */
export const DEFAULT_ADMIN_PAGE_SIZE = 20;
export const ADMIN_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

/**
 * Available flag filters
 */
export const FLAG_FILTERS = ['sibling', 'staff', 'transportation'] as const;
export type FlagFilter = typeof FLAG_FILTERS[number];
